/**
 * The single place the app talks to the API.
 *
 * Holds the bearer token, builds query strings, and turns every failure — HTTP
 * error, network drop, malformed response — into an ApiError with a message that
 * can be shown to a person as-is.
 */

const BASE_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001/api").replace(/\/$/, "");
const TOKEN_KEY = "trustlab_token";

/** An API failure. `status` is 0 when the request never reached the server. */
export class ApiError extends Error {
  constructor(message, status = 0, fields = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fields = fields; // per-field messages from the validation layer, when present
  }
}

let token = readStoredToken();
let onUnauthorized = null;

function readStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null; // private mode / storage disabled
  }
}

export function setAuthToken(next) {
  token = next || null;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* best effort — the in-memory token still works for this session */
  }
}

export const getAuthToken = () => token;

/**
 * Called when the API rejects our token — expired, revoked, or the account was
 * disabled. The store registers a handler that signs the user out.
 */
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

/** Drops empty values so `?search=&page=1` doesn't send a blank filter. */
function queryString(params) {
  if (!params) return "";
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    qs.append(k, v);
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

async function request(method, path, { body, params, signal } = {}) {
  const url = BASE_URL + path + queryString(params);

  let res;
  try {
    res = await fetch(url, {
      method,
      headers: {
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      signal,
    });
  } catch (err) {
    if (err.name === "AbortError") throw err;
    // fetch only rejects when the request never completed
    throw new ApiError("Can't reach the server. Check that the API is running.", 0);
  }

  // 204 and CSV downloads have no JSON body
  const isJson = (res.headers.get("content-type") || "").includes("application/json");
  const payload = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    if (res.status === 401 && onUnauthorized) onUnauthorized();
    throw new ApiError(payload?.error || `Request failed (${res.status})`, res.status, payload?.fields || null);
  }

  return payload;
}

/**
 * Downloads a file from an authenticated endpoint.
 *
 * A plain <a href> can't carry the bearer token, so fetch it, then hand the blob
 * to a temporary link.
 */
async function download(path, params, fallbackName = "download.csv") {
  const res = await fetch(BASE_URL + path + queryString(params), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }).catch(() => {
    throw new ApiError("Can't reach the server. Check that the API is running.", 0);
  });

  if (!res.ok) {
    if (res.status === 401 && onUnauthorized) onUnauthorized();
    const body = await res.json().catch(() => null);
    throw new ApiError(body?.error || `Download failed (${res.status})`, res.status);
  }

  // prefer the filename the server chose
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const blob = await res.blob();

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  get: (path, opts) => request("GET", path, opts),
  post: (path, body, opts) => request("POST", path, { ...opts, body: body ?? {} }),
  patch: (path, body, opts) => request("PATCH", path, { ...opts, body: body ?? {} }),
  del: (path, opts) => request("DELETE", path, opts),
  download,
};

export { BASE_URL };
